import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Pencil, Trash2, Plus, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { BulkBar, RowCheck } from "./BulkBar";

export function AdminBuildingsTab({ onChanged }) {
  const [buildings, setBuildings] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null); // doc | "new"
  const [form, setForm] = useState({ name: "", address: "", code: "", admin_id: "" });
  const [saving, setSaving] = useState(false);

  const [toDelete, setToDelete] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // --- bulk selection ----------------------------------------------------
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDelete, setBulkDelete] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, u] = await Promise.all([
        api.get("/buildings"),
        api.get("/platform/users", { params: { role: "admin" } }),
      ]);
      setBuildings(b.data);
      setAdmins(u.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load buildings");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  function openNew() {
    setForm({ name: "", address: "", code: "", admin_id: "" });
    setEditing("new");
  }
  function openEdit(b) {
    setForm({ name: b.name || "", address: b.address || "", code: b.code || "", admin_id: b.admin_id || "" });
    setEditing(b);
  }

  async function save() {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/buildings", { name: form.name, address: form.address, code: form.code });
        toast.success("Building created");
      } else {
        const body = {};
        if (form.name !== editing.name) body.name = form.name;
        if (form.address !== editing.address) body.address = form.address;
        if (form.code !== editing.code) body.code = form.code;
        if ((form.admin_id || "") !== (editing.admin_id || "")) body.admin_id = form.admin_id;
        await api.patch(`/buildings/${editing.id}`, body);
        toast.success("Building updated");
      }
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function doDelete() {
    if (confirmText !== toDelete.code) {
      toast.error("Confirmation does not match");
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/buildings/${toDelete.id}`, { data: { confirm: confirmText } });
      toast.success("Building deleted");
      setToDelete(null);
      setConfirmText("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  }

  // --- bulk helpers ------------------------------------------------------
  function toggleRow(id, on) {
    setSelected((prev) => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; });
  }
  function toggleAllOnPage(on) {
    setSelected(() => (on ? new Set(buildings.map((b) => b.id)) : new Set()));
  }
  function clearSelection() { setSelected(new Set()); }

  async function runBulkDelete() {
    if (bulkConfirm !== "DELETE") return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/platform/bulk/buildings/delete", {
        ids: Array.from(selected), confirm: "DELETE",
      });
      const failedN = data.failed?.length || 0;
      toast.success(`${data.deleted} deleted${failedN ? ` · ${failedN} failed` : ""}`);
      setBulkDelete(false);
      setBulkConfirm("");
      clearSelection();
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkBusy(false); }
  }

  const allOnPageChecked = buildings.length > 0 && buildings.every((b) => selected.has(b.id));

  return (
    <div className="space-y-4" data-testid="admin-buildings-tab">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">
          {buildings.length} building{buildings.length !== 1 ? "s" : ""} on the platform
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="buildings-refresh">
            <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`}/> Refresh
          </Button>
          <Button onClick={openNew} data-testid="buildings-new">
            <Plus size={14} className="mr-1.5"/> New building
          </Button>
        </div>
      </div>

      <div className="card-stroke p-0 overflow-x-auto">
        <BulkBar count={selected.size} onClear={clearSelection} testId="buildings-bulk-bar">
          <Button size="sm" variant="outline"
                  onClick={() => setBulkDelete(true)}
                  className="text-destructive hover:text-destructive"
                  data-testid="buildings-bulk-delete">
            <Trash2 size={14} className="mr-1.5"/> Delete
          </Button>
        </BulkBar>
        <table className="w-full text-sm" data-testid="buildings-table">
          <thead>
            <tr className="label-tiny text-left border-b border-border">
              <th className="py-3 pl-4 pr-2 w-10">
                <RowCheck
                  checked={allOnPageChecked}
                  onChange={toggleAllOnPage}
                  testId="buildings-select-all"
                  label="Select all buildings"
                />
              </th>
              <th>Name</th><th>Code</th><th>Address</th><th>Admin</th><th>Created</th>
              <th className="text-right pr-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {buildings.length === 0 ? (
              <tr><td colSpan={7} className="py-8 text-center text-muted-foreground" data-testid="buildings-empty">
                {loading ? "Loading…" : "No buildings yet."}
              </td></tr>
            ) : buildings.map((b) => {
              const admin = admins.find((a) => a.id === b.admin_id);
              return (
                <tr key={b.id} className="border-b border-border last:border-0" data-testid={`building-row-${b.id}`}>
                  <td className="py-3 pl-4 pr-2">
                    <RowCheck checked={selected.has(b.id)} onChange={(on)=>toggleRow(b.id, on)}
                              testId={`building-select-${b.id}`}/>
                  </td>
                  <td>{b.name}</td>
                  <td className="font-mono text-xs">{b.code}</td>
                  <td className="text-muted-foreground">{b.address}</td>
                  <td className="text-xs">{admin?.name || <span className="text-muted-foreground">unassigned</span>}</td>
                  <td className="font-mono text-xs">{b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}</td>
                  <td className="text-right pr-4 whitespace-nowrap">
                    <Button size="icon" variant="ghost" onClick={()=>openEdit(b)}
                            data-testid={`building-edit-${b.id}`} aria-label="Edit"><Pencil size={14}/></Button>
                    <Button size="icon" variant="ghost" onClick={()=>{ setToDelete(b); setConfirmText(""); }}
                            data-testid={`building-delete-${b.id}`} aria-label="Delete">
                      <Trash2 size={14} className="text-destructive"/>
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o)=>{ if (!o) setEditing(null); }}>
        <DialogContent data-testid="building-modal">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Create building" : `Edit ${editing?.name || ""}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="label-tiny">Name</Label>
              <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-2" data-testid="building-form-name"/></div>
            <div><Label className="label-tiny">Address</Label>
              <Input value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})} className="mt-2" data-testid="building-form-address"/></div>
            <div><Label className="label-tiny">Code (short, uppercase)</Label>
              <Input value={form.code} onChange={(e)=>setForm({...form, code: e.target.value.toUpperCase()})} className="mt-2 font-mono" data-testid="building-form-code"/></div>
            {editing !== "new" && (
              <div><Label className="label-tiny">Assigned admin</Label>
                <Select value={form.admin_id || "none"} onValueChange={(v)=>setForm({...form, admin_id: v === "none" ? "" : v})}>
                  <SelectTrigger className="mt-2" data-testid="building-form-admin"><SelectValue placeholder="None"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {admins.map((a)=>(<SelectItem key={a.id} value={a.id}>{a.name} · {a.email}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditing(null)} data-testid="building-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.address || !form.code}
                    data-testid="building-form-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toDelete} onOpenChange={(o)=>{ if (!o) { setToDelete(null); setConfirmText(""); } }}>
        <DialogContent data-testid="building-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle size={18}/> Delete {toDelete?.name}
            </DialogTitle>
            <DialogDescription>
              This cascades to every unit, pass, call, activity row and door controller for this building.
              Users whose only building is this one will also be removed. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="label-tiny">Type the building code <span className="font-mono text-foreground">{toDelete?.code}</span> to confirm</Label>
            <Input value={confirmText} onChange={(e)=>setConfirmText(e.target.value.toUpperCase())}
                   className="mt-2 font-mono" autoFocus
                   data-testid="building-delete-confirm-input"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>{ setToDelete(null); setConfirmText(""); }} data-testid="building-delete-cancel">Cancel</Button>
            <Button onClick={doDelete} disabled={deleting || confirmText !== toDelete?.code}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    data-testid="building-delete-confirm">
              {deleting ? "Deleting…" : "Delete building"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDelete} onOpenChange={(o)=>{ if (!o) { setBulkDelete(false); setBulkConfirm(""); } }}>
        <DialogContent data-testid="buildings-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle size={18}/> Delete {selected.size} building{selected.size === 1 ? "" : "s"}
            </DialogTitle>
            <DialogDescription>
              Every selected building and <strong>all of its units, passes, calls, activity, door controllers and
              building-scoped users</strong> will be removed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="label-tiny">Type <span className="font-mono text-foreground">DELETE</span> to confirm</Label>
            <Input value={bulkConfirm} onChange={(e)=>setBulkConfirm(e.target.value.toUpperCase())}
                   className="mt-2 font-mono" autoFocus
                   data-testid="buildings-bulk-delete-confirm-input"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>{ setBulkDelete(false); setBulkConfirm(""); }}
                    data-testid="buildings-bulk-delete-cancel">Cancel</Button>
            <Button onClick={runBulkDelete} disabled={bulkBusy || bulkConfirm !== "DELETE"}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    data-testid="buildings-bulk-delete-confirm">
              {bulkBusy ? "Deleting…" : `Delete ${selected.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
